import {useEffect, useState} from "react";
import { initializeDB } from "../config/db";
import {
    CC_STORE,
    TRAINING_DUE_STORE,
    SSD_STORE,
    JHA_STORE,
    DAILY_LOG_STORE
} from "../config/constants";
import { localDateString } from "../utils/localDate";

export function useMainPageStatuses() {
    const [ccStatus, setCcStatus] = useState('');
    const [trainingStatuses, setTrainingStatuses] = useState({});
    const [ssdStatuses, setSsdStatuses] = useState({});
    const [jhaStatuses, setJhaStatuses] = useState({});
    const [dailyLogStatuses, setDailyLogStatuses] = useState({});

    useEffect(() => {
        async function fetchData() {
            try {
                // Открываем IndexedDB используя централизованную функцию
                const db = await initializeDB();

                // CC статус
                try {
                    const txCc = db.transaction(CC_STORE, 'readonly');
                    const storeCc = txCc.objectStore(CC_STORE);
                    const cc1 = await storeCc.get(1);
                    await txCc.done;

                    setCcStatus(cc1 ? 'In Process' : '');
                } catch (e) {
                    console.error('Error fetching CC:', e);
                }

                // Training статусы
                try {
                    const txTraining = db.transaction(TRAINING_DUE_STORE, 'readonly');
                    const storeTraining = txTraining.objectStore(TRAINING_DUE_STORE);
                    const allTraining = await storeTraining.getAll();
                    await txTraining.done;

                    const trainingMap = {};
                    for (const rec of allTraining) {
                        const st = rec.status || 'pending';
                        trainingMap[st] = (trainingMap[st] || 0) + 1;
                    }
                    setTrainingStatuses(trainingMap);
                } catch (e) {
                    console.error('Error fetching Training:', e);
                }

                // SSD статусы
                try {
                    const txSsd = db.transaction(SSD_STORE, 'readonly');
                    const storeSsd = txSsd.objectStore(SSD_STORE);
                    const allSsd = await storeSsd.getAll();
                    await txSsd.done;

                    const ssdMap = {};
                    for (const rec of allSsd) {
                        const st = rec.status || 'active';
                        ssdMap[st] = (ssdMap[st] || 0) + 1;
                    }
                    setSsdStatuses(ssdMap);
                } catch (e) {
                    console.error('Error fetching SSD:', e);
                }

                // JHA статусы
                try {
                    const txJha = db.transaction(JHA_STORE, 'readonly');
                    const storeJha = txJha.objectStore(JHA_STORE);
                    const allJha = await storeJha.getAll();
                    await txJha.done;

                    const jhaMap = {};
                    for (const rec of allJha) {
                        const st = rec.status || 'draft';
                        jhaMap[st] = (jhaMap[st] || 0) + 1;
                    }
                    setJhaStatuses(jhaMap);
                } catch (e) {
                    console.error('Error fetching JHA:', e);
                }

                // Daily Log статусы
                try {
                    const txDailyLog = db.transaction(DAILY_LOG_STORE, 'readonly');
                    const storeDailyLog = txDailyLog.objectStore(DAILY_LOG_STORE);
                    const allDailyLogs = await storeDailyLog.getAll();
                    await txDailyLog.done;

                    // Filter for today's logs (use LOCAL day, not UTC)
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const todayStr = localDateString(today);
                    
                    const todayLogs = allDailyLogs.filter(log => {
                        if (!log.date) return false;
                        // Handle both date formats - could be YYYY-MM-DD or ISO string
                        const logDateStr = log.date.split('T')[0];
                        return logDateStr === todayStr && !log.deleted;
                    });

                    const dailyLogMap = {
                        pending: 0,
                        synced: 0,
                        failed: 0,
                        total: todayLogs.length
                    };
                    
                    for (const log of todayLogs) {
                        const syncStatus = log.syncStatus || 'pending';
                        if (syncStatus === 'pending') dailyLogMap.pending++;
                        else if (syncStatus === 'synced') dailyLogMap.synced++;
                        else if (syncStatus === 'failed') dailyLogMap.failed++;
                    }
                    
                    setDailyLogStatuses(dailyLogMap);
                } catch (e) {
                    console.error('Error fetching Daily Logs:', e);
                }

            } catch (error) {
                console.error('Ошибка в useMainPageStatuses:', error);
            }
        }
        
        fetchData();

        // Подписываемся на изменения
        const handleStatusChange = () => {
            // Re-fetch data when status changes
            fetchData();
        };

        window.addEventListener('instructionStatusChanged', handleStatusChange);
        window.addEventListener('syncCompleted', handleStatusChange);

        return () => {
            window.removeEventListener('instructionStatusChanged', handleStatusChange);
            window.removeEventListener('syncCompleted', handleStatusChange);
        };
    }, []);

    return { ccStatus, trainingStatuses, ssdStatuses, jhaStatuses, dailyLogStatuses };
}
